import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useDebounceFn } from '@vueuse/core'
import { useJournalsListStore } from '@/views/apps/journals/store/journalsListStore'
import { useJournalsListFilterStore } from '@/views/apps/journals/store/journalsListFilterStore'
import { DEBOUNCE_DELAY_MS } from '@/views/apps/journals/constants/apiEndpoints'
import { TABLE_ITEMS_PER_PAGE_OPTIONS } from '@/views/apps/journals/constants/pagination'
import { DATE_FILTER_PRESETS } from '@/views/apps/journals/constants/dateFilters'
import {
  formatFullName,
  formatPersonName,
} from '@/views/apps/journals/utils/formatters'
import { resolveChoiceLabel } from '@/views/apps/journals/utils/filterTree'
import { useDateFilter } from '@/views/apps/journals/composables/useDateFilter'
import {
  type TableOptions,
  useServerTableOptions,
} from '@/modules/journals/composables/useServerTableOptions'

interface RowPayload {
  item?: { id: number }
  internalItem?: { raw?: { id: number; value?: number }; value?: number }
}

type FilterKey = 'departments' | 'responsible_employees' | 'created_by'
type ChipType = FilterKey | 'created_at' | 'search'
interface FilterChip {
  label: string; type: ChipType; value?: string | number
}

const TREE_FILTER_CHIP_LABELS: Record<FilterKey, string> = {
  departments: 'Подразделение',
  responsible_employees: 'Ответственный',
  created_by: 'Создатель',
}

export function useJournalsListPage() {
  const router = useRouter()
  const store = useJournalsListStore()
  const journalsListFilterStore = useJournalsListFilterStore()

  const {
    page: currentPage,

    currentSort,
    isSamePageRequest,
    markFetched,
    resetToFirstPage,
    updateSort,
  } = useServerTableOptions({
    initialPage: 1,
    initialItemsPerPage: store.itemsPerPage,
    initialSort: null,
  })

  const isAddModalOpen = ref(false)
  const filterMenus = ref<Record<string, boolean>>({})
  const tempSelectedFilters = ref<Record<string, string[]>>({})
  const isCreatedAtMenuOpen = ref(false)

  const createdAtDateFilter = useDateFilter('date')

  const {
    localFrom: createdAtFrom,
    localTo: createdAtTo,
    setDatePreset: setCreatedAtPreset,
  } = createdAtDateFilter

  const { searchQuery, itemsPerPage, isFetching, error, journals, totalItems }
    = storeToRefs(store)

  const displayedItems = computed(() => journals.value)

  const headers = [
    {
      title: 'Название',
      key: 'name',
      align: 'start' as const,
      sortable: true,
      width: '35%',
    },
    {
      title: 'Подразделения',
      key: 'departments',
      align: 'start' as const,
      sortable: false,
    },
    {
      title: 'Ответственные',
      key: 'responsible_employees',
      align: 'start' as const,
      sortable: false,
    },
    {
      title: 'Создатель',
      key: 'created_by',
      align: 'start' as const,
      sortable: true,
    },
    {
      title: 'Дата создания',
      key: 'created_at',
      align: 'start' as const,
      sortable: true,
    },
  ]

  const resetAndFetch = async () => {
    resetToFirstPage()
    store.resetPagination()
    await store.fetchJournals()
    markFetched(currentPage.value, store.itemsPerPage)
  }

  const debouncedSearch = useDebounceFn(() => {
    resetAndFetch()
  }, DEBOUNCE_DELAY_MS)

  watch(searchQuery, () => debouncedSearch())

  onMounted(async () => {
    await store.fetchJournals()
    markFetched(currentPage.value, store.itemsPerPage)
  })

  async function onTableOptionsUpdate(options: TableOptions) {
    const nextPage = options.page ?? currentPage.value
    const nextItemsPerPage = options.itemsPerPage ?? store.itemsPerPage
    const nextSort = options.sortBy?.[0] ?? null

    if (nextSort) {
      if (
        !currentSort.value
        || currentSort.value.key !== nextSort.key
        || currentSort.value.order !== nextSort.order
      ) {
        updateSort(nextSort)
        store.setSort(nextSort.key, nextSort.order)
        resetToFirstPage()
        store.setPage(1)
        store.setCursor(undefined)
        await store.fetchJournals()
        markFetched(1, store.itemsPerPage)

        return
      }
    } else if (store.sortBy !== undefined || store.orderBy !== undefined) {
      updateSort(null)
      store.setSort(undefined, undefined)
      resetToFirstPage()
      store.setPage(1)
      store.setCursor(undefined)
      await store.fetchJournals()
      markFetched(1, store.itemsPerPage)

      return
    }

    if (isSamePageRequest(nextPage, nextItemsPerPage)) {
      return
    }

    if (nextItemsPerPage !== store.itemsPerPage) {
      store.setItemsPerPage(nextItemsPerPage)
      resetToFirstPage()
      store.setPage(1)
      store.setCursor(undefined)
      await store.fetchJournals()
      markFetched(1, nextItemsPerPage)

      return
    }

    currentPage.value = nextPage
    store.setPage(nextPage)
    store.setCursor(undefined)
    await store.fetchJournals()
    markFetched(nextPage, nextItemsPerPage)
  }

  const onRowClick = async (_event: unknown, payload: RowPayload) => {
    const rawItem = payload?.item || payload?.internalItem?.raw
    const idCandidate = rawItem?.id ?? payload?.internalItem?.value
    const rowId = idCandidate != null ? String(idCandidate) : ''
    if (!rowId) {
      return
    }

    const journalId = Number(idCandidate)
    if (!Number.isNaN(journalId)) {
      router.push({ name: 'journal-detail', params: { id: journalId } })
    }
  }

  const onJournalCreated = async () => {
    isAddModalOpen.value = false
    await resetAndFetch()
  }

  function getLabel(field: string, id: string | number): string {
    return resolveChoiceLabel(
      journalsListFilterStore.searchChoices,
      journalsListFilterStore.filterChoices,
      field,
      id,
    )
  }

  const activeFilterChips = computed<FilterChip[]>(() => {
    const chips: FilterChip[] = []
    const f = journalsListFilterStore.filters

    for (const [key, chipLabel] of Object.entries(TREE_FILTER_CHIP_LABELS) as [FilterKey, string][]) {
      f[key]?.forEach(id =>
        chips.push({
          label: `${chipLabel}: ${getLabel(key, id)}`,
          type: key,
          value: id,
        }),
      )
    }

    if (f.created_at?.from || f.created_at?.to) {
      const from = f.created_at.from || '...'
      const to = f.created_at.to || '...'

      chips.push({ label: `Дата: ${from} — ${to}`, type: 'created_at' })
    }

    if (searchQuery.value?.trim()) {
      chips.push({ label: `Поиск: ${searchQuery.value}`, type: 'search' })
    }

    return chips
  })

  const getFilterIcon = (isActive: boolean) =>
    isActive ? 'ri-filter-fill' : 'ri-filter-2-line'

  const hasActiveFilters = computed(() => {
    const f = journalsListFilterStore.filters

    return (
      !!searchQuery.value
      || (Object.keys(TREE_FILTER_CHIP_LABELS) as FilterKey[]).some(
        key => (f[key]?.length ?? 0) > 0,
      )
      || !!f.created_at?.from
      || !!f.created_at?.to
    )
  })

  const setSearchQuery = (value: string) => store.setSearchQuery(value)

  const isTreeFilterLoading = (key: FilterKey) =>
    !!journalsListFilterStore.filterLoading[key]

  const isTreeFilterActive = (key: FilterKey) =>
    (journalsListFilterStore.filters[key]?.length ?? 0) > 0

  const isCreatedAtActive = computed(() =>
    !!journalsListFilterStore.filters.created_at?.from
    || !!journalsListFilterStore.filters.created_at?.to,
  )

  const resetFilters = async () => {
    store.setSearchQuery('')
    journalsListFilterStore.resetTreeFilters()
    await resetAndFetch()
  }

  const handleFilterClick = async (filterKey: FilterKey) => {
    tempSelectedFilters.value[filterKey] = [
      ...(journalsListFilterStore.filters[filterKey] || []),
    ]
    await journalsListFilterStore.fetchFilterChoices('journal', filterKey)
    filterMenus.value[filterKey] = true
  }

  const applyFilter = async (filterKey: FilterKey) => {
    (journalsListFilterStore.filters as Record<string, string[]>)[filterKey] = [
      ...(tempSelectedFilters.value[filterKey] || []),
    ]
    filterMenus.value[filterKey] = false
    await resetAndFetch()
  }

  const openCreatedAtFilter = () => {
    createdAtDateFilter.initFromModel(
      journalsListFilterStore.filters.created_at,
    )
    isCreatedAtMenuOpen.value = true
  }

  const applyCreatedAtFilter = async () => {
    const value = createdAtDateFilter.getValue() as {
      from?: string
      to?: string
    } | null

    journalsListFilterStore.filters.created_at = value
    isCreatedAtMenuOpen.value = false
    await resetAndFetch()
  }

  const removeFilter = async (chip: FilterChip) => {
    if (chip.type === 'search') {
      store.setSearchQuery('')
    } else if (chip.type === 'created_at') {
      journalsListFilterStore.filters.created_at = null
    } else {
      journalsListFilterStore.filters[chip.type]
        = journalsListFilterStore.filters[chip.type].filter(
          id => id !== chip.value,
        )
    }

    await resetAndFetch()
  }

  return {
    activeFilterChips,
    applyCreatedAtFilter,
    applyFilter,
    createdAtFrom,
    createdAtTo,
    currentPage,
    datePresets: DATE_FILTER_PRESETS,
    displayedItems,
    error,
    filterMenus,
    formatFullName,
    formatPersonName,
    getFilterIcon,
    handleFilterClick,
    hasActiveFilters,
    headers,
    isAddModalOpen,
    isCreatedAtActive,
    isCreatedAtMenuOpen,
    isFetching,
    isTreeFilterActive,
    isTreeFilterLoading,
    itemsPerPage,
    onJournalCreated,
    onRowClick,
    onTableOptionsUpdate,
    openCreatedAtFilter,
    removeFilter,
    resetFilters,
    searchQuery,
    setCreatedAtPreset,
    setSearchQuery,
    tableItemsPerPageOptions: TABLE_ITEMS_PER_PAGE_OPTIONS,
    tempSelectedFilters,
    totalItems,
    treeFilterStore: journalsListFilterStore,
  }
}
